import { useEffect, useId } from 'react';

import type { EntityTypeName } from '../core/types';
import { useCrmSubscription } from './CrmSubscriptionProvider';

/**
 * Subscribe to restricted CRM entity types for the lifetime of the calling component.
 * Multiple components can call this hook concurrently — the provider merges all
 * active subscriptions into a single union sent to the server.
 *
 * @param entityTypes - Array of entity type names to subscribe to (e.g., ['ahoy_meeting']).
 *   Must be hardcoded type names, not derived from untrusted input.
 *
 * Usage:
 * ```tsx
 * function CalendarPage() {
 *   useRestrictedSubscription(['ahoy_meeting', 'ahoy_meeting_notes', 'ahoy_booking']);
 *   // ... rest of component
 * }
 * ```
 */
export function useRestrictedSubscription(entityTypes: EntityTypeName[]): void {
  const ctx = useCrmSubscription();
  const id = useId();
  // Normalize (sort + deduplicate) then serialize entity types to avoid
  // re-subscribing on every render when the caller passes a new array
  // reference with the same content, or the same items in a different order.
  // Entity type arrays are small (1-4 items), so this is cheap.
  const serialized = JSON.stringify([...new Set(entityTypes)].sort());

  useEffect(() => {
    if (!ctx) {
      return;
    }

    const parsed = JSON.parse(serialized) as EntityTypeName[];
    ctx.addSubscription(id, parsed);

    return () => {
      ctx.removeSubscription(id);
    };
  }, [ctx, id, serialized]);
}
